import { useState, useEffect, useRef } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import { services } from '../../mocks/services';
import { areas } from '../../mocks/areas';
import { testimonials } from '../../mocks/testimonials';
import { faqs } from '../../mocks/faqs';

export default function HomePage() {
  const [openFaqId, setOpenFaqId] = useState<number | null>(null);
  const [stats, setStats] = useState({ clients: 0, services: 0, years: 0, rating: 0 });
  const [hasAnimated, setHasAnimated] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const statsRef = useRef<HTMLDivElement>(null);

  const whatsappMessage = encodeURIComponent("Hi Sea Pearl Universal, I'd like to book a cleaning service | مرحباً، أود حجز خدمة تنظيف");
  const whatsappLink = `https://api.whatsapp.com/send?phone=96879964466&text=${whatsappMessage}`;

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            animateStats();
          }
        });
      },
      { threshold: 0.4 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, [hasAnimated]);

  const animateStats = () => {
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;
    let currentStep = 0;
    const timer = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      setStats({
        clients: Math.floor(500 * progress),
        services: Math.floor(12 * progress),
        years: Math.floor(8 * progress),
        rating: parseFloat(Math.min(5, 5 * progress).toFixed(1)),
      });
      if (currentStep >= steps) {
        clearInterval(timer);
        setStats({ clients: 500, services: 12, years: 8, rating: 5 });
      }
    }, interval);
  };

  const toggleFaq = (id: number) => setOpenFaqId(openFaqId === id ? null : id);

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    fetch('https://readdy.ai/api/form/d6mo3pc6fojmejgajgvg', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(formData as any).toString(),
    })
      .then((res) => {
        if (res.ok) { setSubmitStatus('success'); form.reset(); }
        else setSubmitStatus('error');
      })
      .catch(() => setSubmitStatus('error'));
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      <Header />
      <WhatsAppButton />

      {/* ── HERO ── */}
      <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        <img
          src="https://public.readdy.ai/ai/img_res/edited_964bc5b1bb997528bccb6381d6a89e37_8c3c8611.jpg"
          alt="Sea Pearl Universal Hero"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#29ABE2]/85 via-[#1a7fa8]/75 to-[#8DC63F]/70"></div>

        {/* Floating bubbles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-24 left-16 w-20 h-20 bg-white/10 rounded-full animate-pulse"></div>
          <div className="absolute top-40 right-24 w-12 h-12 bg-white/15 rounded-full animate-bounce" style={{ animationDelay: '0.5s' }}></div>
          <div className="absolute bottom-32 left-32 w-16 h-16 bg-white/10 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
          <div className="absolute bottom-20 right-16 w-24 h-24 bg-white/10 rounded-full animate-pulse" style={{ animationDelay: '1.5s' }}></div>
        </div>

        <div className="relative z-10 w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center bg-white/20 backdrop-blur-sm border border-white/30 rounded-full px-5 py-2 mb-8">
            <i className="ri-map-pin-2-fill text-white mr-2"></i>
            <span className="text-white text-sm font-medium">Muscat, Sultanate of Oman | مسقط، سلطنة عُمان</span>
          </div>

          <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold text-white mb-5 leading-tight drop-shadow-lg">
            Professional Cleaning<br />Services in Oman
          </h1>
          <p className="text-2xl md:text-3xl text-white/95 mb-5 font-bold" dir="rtl">
            خدمات تنظيف احترافية في مسقط، عُمان
          </p>
          <p className="text-lg md:text-xl text-white/90 mb-4 max-w-3xl mx-auto leading-relaxed">
            Eco-friendly, affordable &amp; reliable cleaning for homes, villas, offices &amp; commercial spaces across Oman.
          </p>
          <p className="text-base md:text-lg text-white/85 mb-10 max-w-3xl mx-auto leading-relaxed" dir="rtl">
            حلول تنظيف صديقة للبيئة وبأسعار معقولة وموثوقة للمنازل والفلل والمكاتب والمساحات التجارية في جميع أنحاء عمان.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <a href={whatsappLink} target="_blank" rel="noopener noreferrer"
              className="px-9 py-4 bg-[#8DC63F] text-white text-lg font-bold rounded-full hover:bg-[#7AB62F] transition-all hover:scale-105 shadow-2xl whitespace-nowrap cursor-pointer flex items-center gap-2">
              <i className="ri-whatsapp-line text-2xl"></i>Book on WhatsApp
            </a>
            <a href="tel:+96879964466"
              className="px-9 py-4 bg-white text-[#29ABE2] text-lg font-bold rounded-full hover:bg-gray-50 transition-all hover:scale-105 shadow-2xl whitespace-nowrap cursor-pointer flex items-center gap-2">
              <i className="ri-phone-fill text-2xl"></i>Call Now
            </a>
          </div>

          {/* Mini trust badges */}
          <div className="flex flex-wrap justify-center gap-6">
            {[
              { icon: 'ri-shield-check-fill', label: 'Fully Insured' },
              { icon: 'ri-leaf-fill', label: 'Eco-Friendly' },
              { icon: 'ri-time-fill', label: 'Same-Day Booking' },
              { icon: 'ri-star-fill', label: '5★ Rated' },
            ].map((b) => (
              <div key={b.label} className="flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2">
                <i className={`${b.icon} text-white text-base`}></i>
                <span className="text-white text-sm font-medium">{b.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 animate-bounce">
          <span className="text-white/70 text-xs">Scroll Down</span>
          <i className="ri-arrow-down-line text-white/70 text-xl"></i>
        </div>
      </section>

      {/* ── STATS BAR ── */}
      <section id="stats" ref={statsRef} className="bg-white py-14 border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: `${stats.clients}+`, labelEn: 'Happy Clients', labelAr: 'عملاء سعداء', color: 'text-[#29ABE2]', icon: 'ri-group-fill' },
              { value: `${stats.services}`, labelEn: 'Services Offered', labelAr: 'خدمات متاحة', color: 'text-[#8DC63F]', icon: 'ri-service-fill' },
              { value: `${stats.years}+`, labelEn: 'Years Experience', labelAr: 'سنوات خبرة', color: 'text-[#29ABE2]', icon: 'ri-award-fill' },
              { value: `${stats.rating}★`, labelEn: 'Client Rating', labelAr: 'تقييم العملاء', color: 'text-[#8DC63F]', icon: 'ri-star-fill' },
            ].map((s) => (
              <div key={s.labelEn} className="text-center group">
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 bg-gray-50 rounded-2xl group-hover:scale-110 transition-transform">
                  <i className={`${s.icon} ${s.color} text-2xl`}></i>
                </div>
                <div className={`text-4xl md:text-5xl font-extrabold ${s.color} mb-1`}>{s.value}</div>
                <div className="text-sm md:text-base text-gray-600 font-medium">{s.labelEn}</div>
                <div className="text-xs text-gray-400" dir="rtl">{s.labelAr}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ABOUT US ── */}
      <section id="about" className="py-24 bg-[#F4FBFF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Text */}
            <div>
              <span className="inline-block bg-[#29ABE2]/10 text-[#29ABE2] text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-5">About Us | من نحن</span>
              <h2 className="text-4xl md:text-5xl font-extrabold text-[#4D4D4D] mb-3 leading-tight">
                Oman's Most Trusted<br />Cleaning Company
              </h2>
              <h3 className="text-2xl font-bold text-[#29ABE2] mb-6" dir="rtl">شركة التنظيف الأكثر ثقة في عُمان</h3>
              <p className="text-base text-gray-700 mb-4 leading-relaxed">
                Sea Pearl Universal is Muscat's premier professional cleaning company, serving homes, villas, offices, and commercial properties across Oman since 2016. Our highly trained team uses the latest equipment and eco-certified products to deliver spotless, hygienic results every single time.
              </p>
              <p className="text-base text-gray-700 mb-6 leading-relaxed">
                From deep cleaning and sofa restoration to pest control and post-construction cleanup — we handle it all with precision, care, and a 100% satisfaction guarantee. Trusted by over 500 happy clients across Muscat, Seeb, Sohar, Nizwa, and beyond.
              </p>
              <p className="text-sm text-gray-600 mb-8 leading-relaxed" dir="rtl">
                سي بيرل يونيفرسال هي شركة التنظيف الاحترافية الرائدة في مسقط، تخدم المنازل والفلل والمكاتب والعقارات التجارية في جميع أنحاء عمان منذ عام 2016. يستخدم فريقنا المدرب بشكل عالٍ أحدث المعدات والمنتجات المعتمدة بيئياً لتقديم نتائج نظيفة وصحية في كل مرة.
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {[
                  { icon: 'ri-team-line', color: 'text-[#29ABE2]', bg: 'bg-[#29ABE2]/10', en: 'Experienced Team', ar: 'فريق ذو خبرة' },
                  { icon: 'ri-leaf-line', color: 'text-[#8DC63F]', bg: 'bg-[#8DC63F]/10', en: 'Eco-Friendly', ar: 'صديق للبيئة' },
                  { icon: 'ri-price-tag-3-line', color: 'text-[#29ABE2]', bg: 'bg-[#29ABE2]/10', en: 'Affordable', ar: 'أسعار معقولة' },
                  { icon: 'ri-shield-check-line', color: 'text-[#8DC63F]', bg: 'bg-[#8DC63F]/10', en: 'Quality Guaranteed', ar: 'جودة مضمونة' },
                  { icon: 'ri-shield-user-line', color: 'text-[#29ABE2]', bg: 'bg-[#29ABE2]/10', en: 'Fully Insured', ar: 'مؤمن بالكامل' },
                  { icon: 'ri-map-pin-user-line', color: 'text-[#8DC63F]', bg: 'bg-[#8DC63F]/10', en: 'Local Experts', ar: 'خبراء محليون' },
                ].map((f) => (
                  <div key={f.en} className="flex items-center gap-3 bg-white rounded-xl p-3 shadow-sm">
                    <div className={`w-9 h-9 flex items-center justify-center ${f.bg} rounded-lg flex-shrink-0`}>
                      <i className={`${f.icon} ${f.color} text-lg`}></i>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 text-xs leading-tight">{f.en}</p>
                      <p className="text-xs text-gray-500" dir="rtl">{f.ar}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Images grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 w-full h-64 rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://public.readdy.ai/ai/img_res/c2491e54de90f07d3d18a366f74fce41.jpg"
                  alt="Sea Pearl Universal Team"
                  className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
              <div className="w-full h-48 rounded-2xl overflow-hidden shadow-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=Close%20up%20of%20professional%20eco-friendly%20cleaning%20products%20and%20equipment%20neatly%20arranged%20on%20a%20white%20surface%2C%20green%20and%20blue%20spray%20bottles%2C%20microfiber%20cloths%2C%20mop%2C%20bucket%2C%20clean%20minimal%20background%2C%20commercial%20product%20photography&width=400&height=300&seq=about-products-002&orientation=landscape"
                  alt="Eco-Friendly Products"
                  className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
              <div className="w-full h-48 rounded-2xl overflow-hidden shadow-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=Sparkling%20clean%20modern%20kitchen%20interior%20after%20professional%20deep%20cleaning%20service%2C%20shiny%20countertops%2C%20spotless%20appliances%2C%20bright%20white%20cabinets%2C%20gleaming%20tiles%2C%20fresh%20and%20hygienic%20atmosphere%2C%20commercial%20photography%20Oman&width=400&height=300&seq=about-result-003&orientation=landscape"
                  alt="Cleaning Results"
                  className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SERVICES ── */}
      <section id="services" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-[#8DC63F]/10 text-[#8DC63F] text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-5">Our Services | خدماتنا</span>
            <h2 className="text-4xl md:text-5xl font-extrabold text-[#4D4D4D] mb-3">12 Professional Cleaning Services</h2>
            <h3 className="text-2xl font-bold text-[#29ABE2] mb-4" dir="rtl">١٢ خدمات تنظيف احترافية</h3>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">
              Comprehensive, eco-friendly cleaning solutions tailored to every need — from deep home cleaning to specialized commercial services.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {services.map((service) => (
              <div key={service.id} className="bg-white border border-gray-100 rounded-2xl overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group">
                {/* Service image */}
                <div className="w-full h-48 overflow-hidden relative">
                  <img
                    src={service.image}
                    alt={service.titleEn}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                  <div className="absolute bottom-3 left-3 w-10 h-10 flex items-center justify-center bg-white/90 rounded-xl shadow">
                    <i className={`${service.icon} text-[#29ABE2] text-xl`}></i>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-[#4D4D4D] mb-1">{service.titleEn}</h3>
                  <h4 className="text-base font-semibold text-[#29ABE2] mb-3" dir="rtl">{service.titleAr}</h4>
                  <p className="text-sm text-gray-600 mb-2 leading-relaxed">{service.descEn}</p>
                  <p className="text-sm text-gray-500 mb-5 leading-relaxed" dir="rtl">{service.descAr}</p>
                  <a href={whatsappLink} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#8DC63F] text-white text-sm font-semibold rounded-full hover:bg-[#7AB62F] transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-whatsapp-line text-base"></i>Book Now
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── AREAS SERVED ── */}
      <section id="areas" className="py-24 bg-[#F4FBFF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <div className="relative">
              <div className="w-full h-96 rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://readdy.ai/api/search-image?query=Beautiful%20panoramic%20aerial%20view%20of%20Muscat%20Oman%20cityscape%20with%20modern%20buildings%2C%20Sultan%20Qaboos%20Grand%20Mosque%20visible%2C%20blue%20sea%20coastline%2C%20mountains%20in%20background%2C%20golden%20hour%20lighting%2C%20stunning%20landscape%20photography%2C%20vibrant%20colors&width=700&height=500&seq=areas-muscat-001&orientation=landscape"
                  alt="Muscat Oman Coverage Area"
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                />
              </div>
              <div className="absolute -bottom-5 -right-5 bg-white rounded-2xl shadow-xl p-5 flex items-center gap-3">
                <div className="w-12 h-12 flex items-center justify-center bg-[#29ABE2] rounded-xl">
                  <i className="ri-map-pin-2-fill text-white text-2xl"></i>
                </div>
                <div>
                  <p className="font-bold text-[#4D4D4D] text-sm">12+ Areas Covered</p>
                  <p className="text-xs text-gray-500" dir="rtl">أكثر من ١٢ منطقة مغطاة</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div>
              <span className="inline-block bg-[#29ABE2]/10 text-[#29ABE2] text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-5">Coverage | التغطية</span>
              <h2 className="text-4xl md:text-5xl font-extrabold text-[#4D4D4D] mb-3 leading-tight">Areas We Serve<br />Across Oman</h2>
              <h3 className="text-2xl font-bold text-[#29ABE2] mb-5" dir="rtl">المناطق التي نخدمها في عُمان</h3>
              <p className="text-base text-gray-600 mb-8 leading-relaxed">
                From the capital Muscat to Sohar, Nizwa, and beyond — Sea Pearl Universal provides professional cleaning services across all major cities and governorates in the Sultanate of Oman.
              </p>
              <div className="flex flex-wrap gap-3">
                {areas.map((area, index) => (
                  <div key={index}
                    className={`px-5 py-2.5 rounded-full border-2 cursor-pointer transition-all hover:scale-105 ${index % 2 === 0 ? 'bg-[#29ABE2] border-[#29ABE2] text-white' : 'bg-white border-[#8DC63F] text-[#8DC63F] hover:bg-[#8DC63F] hover:text-white'}`}>
                    <span className="text-sm font-semibold">{area.nameEn}</span>
                    <span className="mx-1.5 opacity-60">|</span>
                    <span className="text-sm font-semibold" dir="rtl">{area.nameAr}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8 p-4 bg-white rounded-xl border border-[#29ABE2]/20 flex items-center gap-3">
                <i className="ri-information-line text-[#29ABE2] text-xl flex-shrink-0"></i>
                <p className="text-sm text-gray-600">Don't see your area? <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="text-[#29ABE2] font-semibold hover:underline cursor-pointer">Contact us on WhatsApp</a> — we may still be able to help!</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── WHY CHOOSE US ── */}
      <section id="why-us" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-[#8DC63F]/10 text-[#8DC63F] text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-5">Why Us | لماذا نحن</span>
            <h2 className="text-4xl md:text-5xl font-extrabold text-[#4D4D4D] mb-3">Why Choose Sea Pearl Universal</h2>
            <h3 className="text-2xl font-bold text-[#29ABE2] mb-4" dir="rtl">لماذا تختار سي بيرل يونيفرسال</h3>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">
              We don't just clean — we transform your space. Here's what sets us apart from every other cleaning company in Oman.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            {/* Image */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 w-full h-56 rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20cleaning%20team%20leader%20in%20uniform%20inspecting%20a%20spotlessly%20clean%20modern%20living%20room%20interior%2C%20quality%20check%2C%20satisfied%20expression%2C%20bright%20natural%20light%2C%20clean%20white%20walls%2C%20luxury%20furniture%2C%20commercial%20photography%20Oman&width=800&height=350&seq=why-us-main-001&orientation=landscape"
                  alt="Quality Inspection"
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                />
              </div>
              <div className="w-full h-44 rounded-2xl overflow-hidden shadow-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20cleaning%20worker%20in%20blue%20uniform%20using%20advanced%20industrial%20vacuum%20cleaner%20on%20a%20large%20carpet%20in%20a%20bright%20modern%20home%2C%20professional%20equipment%2C%20clean%20result%2C%20commercial%20photography&width=400&height=280&seq=why-us-vacuum-002&orientation=landscape"
                  alt="Advanced Equipment"
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                />
              </div>
              <div className="w-full h-44 rounded-2xl overflow-hidden shadow-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=Eco-friendly%20green%20cleaning%20products%20and%20natural%20ingredients%20arranged%20beautifully%20on%20white%20background%2C%20lemon%2C%20baking%20soda%2C%20vinegar%2C%20green%20plants%2C%20sustainable%20cleaning%20concept%2C%20commercial%20photography&width=400&height=280&seq=why-us-eco-003&orientation=landscape"
                  alt="Eco-Friendly Products"
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                />
              </div>
            </div>

            {/* Cards */}
            <div className="space-y-5">
              {[
                { icon: 'ri-time-line', color: 'text-[#29ABE2]', bg: 'bg-[#29ABE2]/10', en: 'Fast & Reliable — Same-Day Booking', ar: 'سريع وموثوق — حجز في نفس اليوم', desc: 'We offer same-day booking and always arrive on time. Our efficient team completes every job promptly without compromising quality.' },
                { icon: 'ri-star-smile-line', color: 'text-[#8DC63F]', bg: 'bg-[#8DC63F]/10', en: '100% Satisfaction Guaranteed', ar: 'ضمان الرضا 100%', desc: 'If you\'re not completely happy with our service, we\'ll return and make it right at absolutely no extra cost.' },
                { icon: 'ri-shield-check-line', color: 'text-[#29ABE2]', bg: 'bg-[#29ABE2]/10', en: 'Trained, Certified & Insured Staff', ar: 'موظفون مدربون ومعتمدون ومؤمنون', desc: 'All our professionals are rigorously trained, certified, background-checked, and fully insured for your peace of mind.' },
                { icon: 'ri-leaf-line', color: 'text-[#8DC63F]', bg: 'bg-[#8DC63F]/10', en: 'Eco-Friendly, Non-Toxic Products', ar: 'منتجات صديقة للبيئة غير سامة', desc: 'We use only environmentally safe, non-toxic cleaning products — gentle on your family, children, pets, and the planet.' },
                { icon: 'ri-price-tag-3-line', color: 'text-[#29ABE2]', bg: 'bg-[#29ABE2]/10', en: 'Transparent & Competitive Pricing', ar: 'أسعار شفافة وتنافسية', desc: 'Premium cleaning at affordable rates with no hidden fees. Get a free quote tailored to your exact needs.' },
                { icon: 'ri-customer-service-2-line', color: 'text-[#8DC63F]', bg: 'bg-[#8DC63F]/10', en: '24/7 WhatsApp Customer Support', ar: 'دعم العملاء 24/7 عبر واتساب', desc: 'Our support team is available around the clock via WhatsApp and phone to answer questions and schedule services.' },
              ].map((item) => (
                <div key={item.en} className="flex items-start gap-4 bg-[#F4FBFF] rounded-xl p-5 hover:shadow-md transition-shadow">
                  <div className={`w-12 h-12 flex items-center justify-center ${item.bg} rounded-xl flex-shrink-0`}>
                    <i className={`${item.icon} ${item.color} text-2xl`}></i>
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-[#4D4D4D] mb-0.5">{item.en}</h3>
                    <p className="text-xs font-semibold text-[#29ABE2] mb-2" dir="rtl">{item.ar}</p>
                    <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── WHO WE ARE / LEADERSHIP ── */}
      <section id="who-we-are" className="py-24 bg-[#F4FBFF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-[#29ABE2]/10 text-[#29ABE2] text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-5">Leadership | القيادة</span>
            <h2 className="text-4xl md:text-5xl font-extrabold text-[#4D4D4D] mb-3">Meet Our Leadership</h2>
            <h3 className="text-2xl font-bold text-[#29ABE2] mb-4" dir="rtl">تعرف على قيادتنا</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {/* Chairman */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-shadow">
              <div className="p-7">
                <div className="inline-flex items-center bg-[#29ABE2]/10 text-[#29ABE2] text-xs font-bold px-3 py-1 rounded-full mb-3">Chairman | رئيس مجلس الإدارة</div>
                <h3 className="text-2xl font-extrabold text-[#4D4D4D] mb-1">Ali Mubarak Musabah Al Rushaidi</h3>
                <p className="text-sm text-gray-500 mb-4">علي مبارك مسبح الرشيدي</p>
                <p className="text-sm text-gray-600 mb-5 leading-relaxed">
                  With a visionary leadership approach, Ali Mubarak founded Sea Pearl Universal with a mission to elevate cleaning standards across Oman. His commitment to quality, integrity, and customer satisfaction has made the company a trusted name in the industry.
                </p>
                <div className="space-y-2">
                  <a href="mailto:seapearluniver@gmail.com" className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#29ABE2] transition-colors cursor-pointer">
                    <i className="ri-mail-fill text-[#29ABE2]"></i>seapearluniver@gmail.com
                  </a>
                </div>
              </div>
            </div>

            {/* General Manager */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-shadow">
              <div className="p-7">
                <div className="inline-flex items-center bg-[#8DC63F]/10 text-[#8DC63F] text-xs font-bold px-3 py-1 rounded-full mb-3">General Manager | المدير العام</div>
                <h3 className="text-2xl font-extrabold text-[#4D4D4D] mb-1">Mohammed Farhan Manaf</h3>
                <p className="text-sm text-gray-500 mb-4">محمد فرحان مناف</p>
                <p className="text-sm text-gray-600 mb-5 leading-relaxed">
                  Mohammed Farhan brings operational excellence and deep industry expertise to Sea Pearl Universal. He oversees day-to-day operations, team training, and quality assurance — ensuring every client receives a world-class cleaning experience.
                </p>
                <div className="space-y-2">
                  <a href="mailto:seapearluniver@gmail.com" className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#8DC63F] transition-colors cursor-pointer">
                    <i className="ri-mail-fill text-[#8DC63F]"></i>seapearluniver@gmail.com
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Mission banner */}
          <div className="relative rounded-2xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Wide%20panoramic%20view%20of%20a%20beautifully%20clean%20modern%20Omani%20villa%20interior%20with%20large%20windows%20overlooking%20a%20lush%20garden%2C%20bright%20natural%20light%2C%20pristine%20white%20walls%2C%20elegant%20furniture%2C%20fresh%20and%20airy%20atmosphere%2C%20luxury%20real%20estate%20photography&width=1200&height=400&seq=mission-bg-001&orientation=landscape"
              alt="Our Mission"
              className="w-full h-64 object-cover object-top"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#29ABE2]/90 to-[#8DC63F]/85"></div>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8">
              <h3 className="text-2xl md:text-3xl font-extrabold text-white mb-3">Our Mission | مهمتنا</h3>
              <p className="text-sm md:text-base text-white/90 max-w-3xl leading-relaxed mb-2">
                To create cleaner, healthier, and more comfortable spaces for every client in Oman through professional expertise, eco-friendly practices, and exceptional customer service.
              </p>
              <p className="text-sm text-white/80 max-w-3xl leading-relaxed" dir="rtl">
                خلق مساحات أنظف وأكثر صحة وراحة لكل عميل في عمان من خلال الخبرة المهنية والممارسات الصديقة للبيئة وخدمة العملاء الاستثنائية.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-[#29ABE2]/10 text-[#29ABE2] text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-5">Reviews | التقييمات</span>
            <h2 className="text-4xl md:text-5xl font-extrabold text-[#4D4D4D] mb-3">What Our Clients Say</h2>
            <h3 className="text-2xl font-bold text-[#29ABE2] mb-4" dir="rtl">ماذا يقول عملاؤنا</h3>
            <p className="text-base text-gray-600 max-w-xl mx-auto">Over 500 happy clients across Oman trust Sea Pearl Universal for all their cleaning needs.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {testimonials.map((t) => (
              <div key={t.id} className="bg-[#F4FBFF] rounded-2xl p-7 shadow-md hover:shadow-xl transition-shadow flex flex-col">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(t.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-[#FFD700] text-lg"></i>
                  ))}
                  <span className="ml-2 text-xs text-gray-500 font-medium">{t.serviceUsed}</span>
                </div>

                {t.language === 'ar' ? (
                  <p className="text-sm text-gray-700 leading-relaxed italic mb-5 flex-1" dir="rtl">"{t.reviewAr}"</p>
                ) : (
                  <p className="text-sm text-gray-700 leading-relaxed italic mb-5 flex-1">"{t.reviewEn}"</p>
                )}

                <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
                  <div>
                    {t.language === 'ar' ? (
                      <h4 className="font-bold text-[#4D4D4D] text-sm" dir="rtl">{t.nameAr}</h4>
                    ) : (
                      <h4 className="font-bold text-[#4D4D4D] text-sm">{t.nameEn}</h4>
                    )}
                    <p className="text-xs text-[#29ABE2] font-medium flex items-center gap-1 mt-0.5">
                      <i className="ri-map-pin-line text-xs"></i>{t.location}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-24 bg-[#F4FBFF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Left: image + CTA */}
            <div className="lg:sticky lg:top-28">
              <span className="inline-block bg-[#8DC63F]/10 text-[#8DC63F] text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-5">FAQ | الأسئلة الشائعة</span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#4D4D4D] mb-3 leading-tight">Frequently Asked Questions</h2>
              <h3 className="text-xl md:text-2xl font-bold text-[#29ABE2] mb-5" dir="rtl">الأسئلة الشائعة</h3>
              <p className="text-base text-gray-600 mb-8 leading-relaxed">
                Have questions about our cleaning services? Find answers to the most common questions below, or contact us directly on WhatsApp.
              </p>
              <div className="w-full h-56 md:h-64 rounded-2xl overflow-hidden shadow-xl mb-8">
                <img
                  src="https://readdy.ai/api/search-image?query=Friendly%20professional%20cleaning%20company%20customer%20service%20representative%20in%20blue%20uniform%20smiling%20and%20talking%20on%20phone%2C%20bright%20modern%20office%20background%2C%20helpful%20and%20approachable%2C%20commercial%20photography%20Oman&width=600&height=400&seq=faq-support-001&orientation=landscape"
                  alt="Customer Support"
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                />
              </div>
              <a href={whatsappLink} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#8DC63F] text-white font-bold rounded-full hover:bg-[#7AB62F] transition-colors whitespace-nowrap cursor-pointer shadow-lg">
                <i className="ri-whatsapp-line text-xl"></i>Ask Us on WhatsApp
              </a>
            </div>

            {/* Right: accordion */}
            <div className="space-y-3">
              {faqs.map((faq) => (
                <div key={faq.id} className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
                  <button
                    onClick={() => toggleFaq(faq.id)}
                    className="w-full px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <div className="flex-1 pr-3">
                      <h3 className="text-sm sm:text-base font-bold text-[#4D4D4D] mb-1 leading-snug">{faq.questionEn}</h3>
                      <p className="text-xs sm:text-sm font-medium text-[#29ABE2]" dir="rtl">{faq.questionAr}</p>
                    </div>
                    <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 transition-colors ${openFaqId === faq.id ? 'bg-[#29ABE2] text-white' : 'bg-gray-100 text-gray-500'}`}>
                      <i className={`${openFaqId === faq.id ? 'ri-subtract-line' : 'ri-add-line'} text-lg`}></i>
                    </div>
                  </button>
                  {openFaqId === faq.id && (
                    <div className="px-4 sm:px-6 pb-5 border-t border-gray-100">
                      <p className="text-sm text-gray-700 leading-relaxed mt-4 mb-3">{faq.answerEn}</p>
                      <p className="text-sm text-gray-600 leading-relaxed" dir="rtl">{faq.answerAr}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── BOOKING CTA BANNER ── */}
      <section className="relative py-24 overflow-hidden bg-gradient-to-br from-[#1a2a3a] via-[#1e3a2a] to-[#1a2a3a]">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#29ABE2] rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#8DC63F] rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-4 leading-tight">
            Ready for a Spotlessly<br />Clean Space?
          </h2>
          <h3 className="text-2xl md:text-3xl font-bold text-white/95 mb-6" dir="rtl">هل أنت مستعد لمساحة نظيفة تماماً؟</h3>
          <p className="text-lg text-white/90 mb-4 max-w-2xl mx-auto">
            Contact Sea Pearl Universal today for a free quote. Same-day booking available across all of Oman.
          </p>
          <p className="text-base text-white/80 mb-10 max-w-2xl mx-auto" dir="rtl">
            تواصل مع سي بيرل يونيفرسال اليوم للحصول على عرض أسعار مجاني. الحجز في نفس اليوم متاح في جميع أنحاء عمان.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href={whatsappLink} target="_blank" rel="noopener noreferrer"
              className="px-10 py-4 bg-white text-[#8DC63F] text-lg font-extrabold rounded-full hover:bg-gray-50 transition-all hover:scale-105 shadow-2xl whitespace-nowrap cursor-pointer flex items-center gap-2">
              <i className="ri-whatsapp-line text-2xl"></i>Book on WhatsApp
            </a>
            <a href="tel:+96879964466"
              className="px-10 py-4 bg-[#4D4D4D] text-white text-lg font-extrabold rounded-full hover:bg-[#3D3D3D] transition-all hover:scale-105 shadow-2xl whitespace-nowrap cursor-pointer flex items-center gap-2">
              <i className="ri-phone-fill text-2xl"></i>Call +968 79964466
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
